/**
 * ContentEditorTab (Issue #1, Paket A/C-UI)
 * Inline-CMS-Editor für White-Label-Seiteninhalte pro Mandant.
 *
 * Semantik (Serververtrag functions/api/admin/content.js):
 * - Leerer Textwert ('') oder DELETE setzt den Block auf den Standardwert (Fallback).
 * - Bildblöcke brauchen eine gültige URL (https://… oder /Pfad). Zum
 *   Zurücksetzen eines Bildes wird der Block geleert.
 * - Funktionen für 'Auf Standard zurücksetzen' pro Slot, pro Seite und für den gesamten Shop
 *   mit Bestätigungsdialogen.
 * - Alle UI-Texte auf Deutsch mit verständlichen Fehlermeldungen bei abgelehnten Slots.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Save,
  CheckCircle2,
  AlertTriangle,
  Image as ImageIcon,
  Upload,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert.jsx';
import {
  CONTENT_PAGE_KEYS,
  CONTENT_FALLBACKS,
  REGISTERED_SLOTS,
  fetchContent,
  saveContent,
  resetSlotContent,
  resetPageContent,
  resetAllContent,
  uploadContentImage,
  toContentDraft,
  formatContentErrorMessage,
} from '../../lib/content.js';

/** Feldkonfiguration pro Seite (Reihenfolge = sort). */
const PAGE_FIELDS = {
  home: [
    { key: 'hero_title', label: 'Hero-Titel', type: 'text' },
    { key: 'hero_subtitle', label: 'Hero-Untertitel', type: 'text', multiline: true },
    { key: 'hero_cta_primary', label: 'Hero-CTA 1: Gesamten Katalog durchsuchen', type: 'text' },
    { key: 'hero_cta_secondary', label: 'Hero-CTA 2: Projekt anfragen', type: 'text' },
    { key: 'trust_1_title', label: 'Vertrauenspunkt 1 (Fachberatung) – Titel', type: 'text' },
    { key: 'trust_1_desc', label: 'Vertrauenspunkt 1 (Fachberatung) – Beschreibung', type: 'text', multiline: true },
    { key: 'trust_2_title', label: 'Vertrauenspunkt 2 (B2B-Sortiment) – Titel', type: 'text' },
    { key: 'trust_2_desc', label: 'Vertrauenspunkt 2 (B2B-Sortiment) – Beschreibung', type: 'text', multiline: true },
    { key: 'trust_3_title', label: 'Vertrauenspunkt 3 (Projektanfragen) – Titel', type: 'text' },
    { key: 'trust_3_desc', label: 'Vertrauenspunkt 3 (Projektanfragen) – Beschreibung', type: 'text', multiline: true },
    { key: 'section_categories_title', label: 'Abschnitt „Fachbereiche“ – Titel', type: 'text' },
    { key: 'section_categories_subtitle', label: 'Abschnitt „Fachbereiche“ – Untertitel', type: 'text', multiline: true },
    { key: 'hero_background', label: 'Hero-Hintergrundbild', type: 'image' },
    { key: 'hero_background_strength', label: 'Hero-Hintergrund-Deckkraft', type: 'text' },
  ],
  catalog: [
    { key: 'catalog_title', label: 'Katalog-Titel', type: 'text' },
    { key: 'catalog_subtitle', label: 'Katalog-Untertitel', type: 'text', multiline: true },
  ],
  product: [
    { key: 'product_inquiry_cta', label: 'Produkt-Anfrage CTA', type: 'text' },
  ],
  footer: [
    { key: 'footer_description', label: 'Footer-Beschreibung (unter Logo)', type: 'text', multiline: true },
    { key: 'footer_partner_badge', label: 'Partner-Badge Text', type: 'text' },
    { key: 'footer_b2b_notice', label: 'B2B-Hinweiszeile', type: 'text' },
    { key: 'footer_copyright', label: 'Copyright-Zeile', type: 'text' },
  ],
  legal: [
    { key: 'imprint_title', label: 'Impressum – Titel', type: 'text' },
    { key: 'imprint_content', label: 'Impressum – Inhalt', type: 'text', multiline: true },
    { key: 'privacy_title', label: 'Datenschutz – Titel', type: 'text' },
    { key: 'privacy_content', label: 'Datenschutz – Inhalt', type: 'text', multiline: true },
    { key: 'agb_title', label: 'AGB & Widerruf – Titel', type: 'text' },
    { key: 'agb_content', label: 'AGB & Widerruf – Inhalt', type: 'text', multiline: true },
    { key: 'shipping_title', label: 'Versand & Lieferung – Titel', type: 'text' },
    { key: 'shipping_content', label: 'Versand & Lieferung – Inhalt', type: 'text', multiline: true },
  ],
  brand: [
    { key: 'logo', label: 'Shop-Logo', type: 'image' },
  ],
  onboarding: [
    { key: 'onboarding_guide', label: '„So bearbeiten Sie Ihren Shop“-Kurzanleitung', type: 'text', multiline: true },
  ],
};

const PAGE_LABELS = {
  home: 'Startseite',
  catalog: 'Katalog & Fachbereiche',
  product: 'Produktdetails',
  footer: 'Footer & Fußbereich',
  legal: 'Rechtliches & Pflichtangaben',
  brand: 'Marken-Branding (Logo)',
  onboarding: 'Kurzanleitung für Interessenten',
};

export function ContentEditorTab({
  activeTenant,
  apiKey,
  saving = false,
}) {
  const [page, setPage] = useState('home');
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingPage, setSavingPage] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Bestätigungszustände für Zurücksetzen
  const [confirmResetSlot, setConfirmResetSlot] = useState(null);
  const [confirmResetPage, setConfirmResetPage] = useState(false);
  const [confirmResetAll, setConfirmResetAll] = useState(false);

  const fields = PAGE_FIELDS[page] || [];
  const fallbacks = CONTENT_FALLBACKS[page] || {};

  const loadPage = useCallback(async () => {
    if (!activeTenant?.slug) return;
    setLoading(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    setConfirmResetSlot(null);
    setConfirmResetPage(false);
    setConfirmResetAll(false);
    try {
      const res = await fetchContent(activeTenant.slug, page);
      setDraft(toContentDraft(PAGE_FIELDS[page] || [], res.blocks));
    } catch (err) {
      // Fallback: leere Entwurfsfelder (Platzhalter = hartkodierte Texte)
      setDraft(toContentDraft(PAGE_FIELDS[page] || [], []));
      setErrorMsg(formatContentErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [activeTenant?.slug, page]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const setFieldValue = (key, patch) => {
    setDraft((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { type: 'text', value: '', image_url: '' }), ...patch },
    }));
  };

  const handleUpload = async (fieldKey, file) => {
    if (!file) return;
    setUploadingKey(fieldKey);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const res = await uploadContentImage(file, apiKey || undefined);
      setFieldValue(fieldKey, { type: 'image', image_url: res.image_url });
      setSuccessMsg('Bild hochgeladen. Klicken Sie auf „Speichern“, um die Änderung zu veröffentlichen.');
    } catch (err) {
      setErrorMsg(formatContentErrorMessage(err));
    } finally {
      setUploadingKey(null);
    }
  };

  const handleSave = async () => {
    if (!activeTenant?.id) {
      setErrorMsg('Kein Mandant ausgewählt.');
      return;
    }
    setSavingPage(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      // Sämtliche Felder der Seite mitschicken: leerer Text = Standard/Fallback.
      const blocks = fields.map((field, index) => {
        const entry = draft[field.key] || { type: field.type, value: '', image_url: '' };
        if (field.type === 'image') {
          return {
            block_key: field.key,
            type: entry.image_url && String(entry.image_url).trim() ? 'image' : 'text',
            value: entry.image_url && String(entry.image_url).trim() ? '' : '',
            image_url: entry.image_url && String(entry.image_url).trim() ? String(entry.image_url).trim() : undefined,
            sort: index,
          };
        }
        return {
          block_key: field.key,
          type: 'text',
          value: typeof entry.value === 'string' ? entry.value : '',
          sort: index,
        };
      });
      const payloadBlocks = blocks.map((b) => {
        const out = { block_key: b.block_key, type: b.type, sort: b.sort };
        if (b.type === 'image' && b.image_url) out.image_url = b.image_url;
        if (b.type === 'text') out.value = b.value;
        return out;
      });
      await saveContent(activeTenant.id, page, payloadBlocks, apiKey || undefined);
      setSuccessMsg('Gespeichert. Die Änderungen sind sofort im Shop sichtbar.');
      await loadPage();
    } catch (err) {
      setErrorMsg(formatContentErrorMessage(err));
    } finally {
      setSavingPage(false);
    }
  };

  // Einzelnen Slot zurücksetzen (mit Bestätigung)
  const handleResetSlot = async (fieldKey, fieldType) => {
    setConfirmResetSlot(null);
    setErrorMsg(null);
    setSuccessMsg(null);

    // Wenn Mandant vorhanden, direkt auf dem Server zurücksetzen
    if (activeTenant?.id) {
      setResetting(true);
      try {
        await resetSlotContent(activeTenant.id, page, fieldKey, apiKey || undefined);
        setFieldValue(fieldKey, fieldType === 'image' ? { image_url: '' } : { value: '' });
        setSuccessMsg(`Slot „${fieldKey}“ wurde auf den Standardwert zurückgesetzt.`);
        await loadPage();
      } catch (err) {
        // Fallback: Entwurfsfeld leeren und Meldung zeigen
        setFieldValue(fieldKey, fieldType === 'image' ? { image_url: '' } : { value: '' });
        setErrorMsg(formatContentErrorMessage(err));
      } finally {
        setResetting(false);
      }
    } else {
      setFieldValue(fieldKey, fieldType === 'image' ? { image_url: '' } : { value: '' });
      setSuccessMsg('Feld zurückgesetzt. Klicken Sie auf „Speichern“, um zu veröffentlichen.');
    }
  };

  // Gesamte Seite zurücksetzen (mit Bestätigung)
  const handleResetPage = async () => {
    if (!activeTenant?.id) {
      setErrorMsg('Kein Mandant ausgewählt.');
      return;
    }
    setConfirmResetPage(false);
    setResetting(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      await resetPageContent(activeTenant.id, page, apiKey || undefined);
      setSuccessMsg(`Alle Inhalte der Seite „${PAGE_LABELS[page] || page}“ wurden auf den Standardwert zurückgesetzt.`);
      await loadPage();
    } catch (err) {
      setErrorMsg(formatContentErrorMessage(err));
    } finally {
      setResetting(false);
    }
  };

  // Gesamten Shop zurücksetzen (mit Bestätigung)
  const handleResetAll = async () => {
    if (!activeTenant?.id) {
      setErrorMsg('Kein Mandant ausgewählt.');
      return;
    }
    setConfirmResetAll(false);
    setResetting(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      await resetAllContent(activeTenant.id, apiKey || undefined);
      setSuccessMsg('Alle Inhalte des gesamten Shops wurden auf die Standardwerte zurückgesetzt.');
      await loadPage();
    } catch (err) {
      setErrorMsg(formatContentErrorMessage(err));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="content-editor-tab">
      {/* Seitenauswahl */}
      <div className="flex flex-wrap items-center gap-2">
        {CONTENT_PAGE_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setPage(key)}
            className={`rounded-lg px-3 py-2 text-xs font-semibold transition-all cursor-pointer ${
              page === key
                ? 'bg-[var(--primary)] text-white shadow-xs'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            {PAGE_LABELS[key] || key}
          </button>
        ))}
      </div>

      {successMsg && (
        <Alert data-testid="content-editor-success">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <AlertTitle>Erfolg</AlertTitle>
          <AlertDescription>{successMsg}</AlertDescription>
        </Alert>
      )}
      {errorMsg && (
        <Alert variant="destructive" data-testid="content-editor-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Fehlermeldung</AlertTitle>
          <AlertDescription>{errorMsg}</AlertDescription>
        </Alert>
      )}

      {/* Bestätigungsdialog: Gesamte Seite zurücksetzen */}
      {confirmResetPage && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="content-reset-page-dialog">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="text-sm font-bold text-amber-900">
                Seite „{PAGE_LABELS[page] || page}“ auf Standard zurücksetzen?
              </h4>
              <p className="text-xs text-amber-800 leading-relaxed">
                Möchten Sie wirklich alle Texte und Bilder dieser Seite auf den Standardzustand zurücksetzen?
                Sämtliche individuellen Anpassungen dieser Seite werden entfernt.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleResetPage}
              disabled={resetting}
              data-testid="content-reset-page-confirm"
            >
              {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
              Ja, gesamte Seite zurücksetzen
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmResetPage(false)}
              disabled={resetting}
              data-testid="content-reset-page-cancel"
            >
              Abbrechen
            </Button>
          </div>
        </div>
      )}

      {/* Bestätigungsdialog: Gesamten Shop zurücksetzen */}
      {confirmResetAll && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4 space-y-3" data-testid="content-reset-all-dialog">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="text-sm font-bold text-red-900">
                Gesamten Shop auf Standard zurücksetzen?
              </h4>
              <p className="text-xs text-red-800 leading-relaxed">
                Achtung: Dies setzt alle Inhalte über sämtliche Seiten (Startseite, Katalog, Footer, Rechtstexte etc.)
                auf die Standardwerte zurück. Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleResetAll}
              disabled={resetting}
              data-testid="content-reset-all-confirm"
            >
              {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
              Ja, gesamten Shop zurücksetzen
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmResetAll(false)}
              disabled={resetting}
              data-testid="content-reset-all-cancel"
            >
              Abbrechen
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle className="font-display text-xl font-bold">
              Inhalte: {PAGE_LABELS[page] || page}
            </CardTitle>
            <CardDescription>
              Leere Felder bedeuten: Der Shop zeigt den Standardtext. Änderungen werden nach dem Speichern
              sofort ohne Neubau veröffentlicht.
            </CardDescription>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setConfirmResetPage(true);
                setConfirmResetAll(false);
              }}
              disabled={loading || savingPage || resetting}
              title="Alle Felder dieser Seite auf den Standardwert zurücksetzen"
              data-testid="content-reset-page-btn"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Seite zurücksetzen
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="content-editor-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Inhalte werden geladen …
            </div>
          )}

          {!loading && fields.map((field) => {
            const entry = draft[field.key] || { type: field.type, value: '', image_url: '' };
            const fallbackValue = fallbacks[field.key] || '';
            const isConfirmingReset = confirmResetSlot === field.key;

            if (field.type === 'image') {
              return (
                <div key={field.key} className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/50 p-3" data-testid={`content-field-${field.key}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <label className="text-sm font-semibold text-slate-700" htmlFor={`content-${field.key}`}>
                      {field.label}
                    </label>
                    <button
                      type="button"
                      onClick={() => setConfirmResetSlot(isConfirmingReset ? null : field.key)}
                      className="text-xs text-slate-500 hover:text-red-700 underline cursor-pointer"
                      title="Auf Standard-Bild zurücksetzen"
                      data-testid={`content-reset-btn-${field.key}`}
                    >
                      Auf Standard zurücksetzen
                    </button>
                  </div>

                  {isConfirmingReset && (
                    <div className="flex items-center justify-between gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid={`content-reset-confirm-${field.key}`}>
                      <span>Diesen Bild-Slot wirklich auf den Standardwert zurücksetzen?</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleResetSlot(field.key, 'image')}
                          className="font-bold text-red-700 hover:underline cursor-pointer"
                          data-testid={`content-reset-confirm-yes-${field.key}`}
                        >
                          Ja, zurücksetzen
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmResetSlot(null)}
                          className="text-slate-600 hover:underline cursor-pointer"
                          data-testid={`content-reset-confirm-no-${field.key}`}
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex h-12 w-12 items-center justify-center rounded-md border border-[var(--border)] bg-white overflow-hidden">
                      {entry.image_url
                        ? <img src={entry.image_url} alt={field.label} className="h-full w-full object-contain" />
                        : <ImageIcon className="h-5 w-5 text-slate-400" />}
                    </div>
                    <Input
                      id={`content-${field.key}`}
                      value={entry.image_url || ''}
                      onChange={(e) => setFieldValue(field.key, { type: 'image', image_url: e.target.value })}
                      placeholder="https://… oder /pfad/zum/bild.png — leer = Standard-Logo"
                      className="flex-1 min-w-[240px] bg-white"
                    />
                    <label className="cursor-pointer inline-flex">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          handleUpload(field.key, file);
                          e.target.value = '';
                        }}
                      />
                      <span className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
                        {uploadingKey === field.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        Bild hochladen
                      </span>
                    </label>
                  </div>
                  {fallbackValue === '' && !entry.image_url && (
                    <p className="text-xs text-slate-500">Aktuell: Standard-Bild des Mandanten.</p>
                  )}
                </div>
              );
            }

            return (
              <div key={field.key} className="space-y-1.5 rounded-lg border border-slate-100 bg-slate-50/50 p-3" data-testid={`content-field-${field.key}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <label className="text-sm font-semibold text-slate-700" htmlFor={`content-${field.key}`}>
                    {field.label}
                  </label>
                  <button
                    type="button"
                    onClick={() => setConfirmResetSlot(isConfirmingReset ? null : field.key)}
                    className="text-xs text-slate-500 hover:text-red-700 underline cursor-pointer"
                    title="Feld auf Standardtext zurücksetzen"
                    data-testid={`content-reset-btn-${field.key}`}
                  >
                    Auf Standard zurücksetzen
                  </button>
                </div>

                {isConfirmingReset && (
                  <div className="flex items-center justify-between gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid={`content-reset-confirm-${field.key}`}>
                    <span>Diesen Slot wirklich auf den Standardtext zurücksetzen?</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleResetSlot(field.key, 'text')}
                        className="font-bold text-red-700 hover:underline cursor-pointer"
                        data-testid={`content-reset-confirm-yes-${field.key}`}
                      >
                        Ja, zurücksetzen
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmResetSlot(null)}
                        className="text-slate-600 hover:underline cursor-pointer"
                        data-testid={`content-reset-confirm-no-${field.key}`}
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}

                {field.multiline ? (
                  <textarea
                    id={`content-${field.key}`}
                    value={entry.value || ''}
                    onChange={(e) => setFieldValue(field.key, { type: 'text', value: e.target.value })}
                    placeholder={String(fallbackValue) || 'Standardtext'}
                    rows={3}
                    className="w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30"
                  />
                ) : (
                  <Input
                    id={`content-${field.key}`}
                    value={entry.value || ''}
                    onChange={(e) => setFieldValue(field.key, { type: 'text', value: e.target.value })}
                    placeholder={String(fallbackValue) || 'Standardtext'}
                    className="bg-white"
                  />
                )}
              </div>
            );
          })}
        </CardContent>

        <CardContent className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border)] pt-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setConfirmResetAll(true);
              setConfirmResetPage(false);
            }}
            disabled={savingPage || saving || loading || resetting}
            className="text-xs text-red-700 hover:text-red-800 hover:bg-red-50"
            data-testid="content-reset-all-btn"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Gesamten Shop auf Standard zurücksetzen
          </Button>

          <Button
            type="button"
            onClick={handleSave}
            disabled={savingPage || saving || loading || resetting}
            data-testid="content-editor-save"
          >
            {savingPage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern &amp; veröffentlichen
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
